import { appNoOp, appStart, onError } from '@actions/app.action';
import { updateCultureCode } from '@actions/culture.action';
import {
  resourceGetAllResources,
  resourceGetCountries,
  resourceGetCountriesSuccess,
  resourceGetCultures,
  resourceGetCulturesSuccess,
  resourceGetCurrencies,
  resourceGetCurrenciesSuccess,
  resourceGetGenders,
  resourceGetGendersSuccess,
  resourceGetProvinceStates,
  resourceGetProvinceStatesSuccess,
  resourceGetSuffixes,
  resourceGetSuffixesSuccess,
  resourceGetTimeZones,
  resourceGetTimeZonesSuccess,
} from '@actions/resource.action';
import { Injectable } from '@angular/core';
import { CULTURE_ROUTE_PARAM, CULTURE_US_SPANISH, CultureKeyType } from '@constants/app.constants';
import { BaseError, ErrorType } from '@models/app.models';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  selectIsCultureValidAndEnabled,
  selectRouteParam,
} from '@selectors/router.selector';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { ResourceService } from '@services/resource/resource.service';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

@Injectable()
export class ResourceEffect {
  getCultures$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(appStart),
      concatLatestFrom(() => this.store.select(selectIsCultureValidAndEnabled)),
      mergeMap(([, isValid]: [Action, boolean]) => {
        if (!isValid) {
          return of(resourceGetCultures());
        }
        return of(appNoOp());
      }),
    );
  });

  getCountryResources$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(resourceGetAllResources, resourceGetCountries, updateCultureCode),
      concatLatestFrom(() => [
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
      ]),
      mergeMap(([, culture]: [Action, CultureKeyType | undefined]) => {
        return this.resourceService.getCountries(culture).pipe(
          switchMap(({ data: countries }) =>
            of(
              resourceGetCountriesSuccess({
                countries,
              }),
            ),
          ),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.ResourcesFetchFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  getProvinceStateResources$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        resourceGetAllResources,
        resourceGetProvinceStates,
        updateCultureCode,
      ),
      concatLatestFrom(() => [
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
      ]),
      mergeMap(([, culture]: [Action, CultureKeyType | undefined]) => {
        return this.resourceService.getProvinceStates(culture).pipe(
          switchMap(({ data: provinceStates }) =>
            of(
              resourceGetProvinceStatesSuccess({
                provinceStates,
              }),
            ),
          ),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.ResourcesFetchFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  getCultureResources$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(resourceGetAllResources, resourceGetCultures, updateCultureCode),
      concatLatestFrom(() => [
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
      ]),
      mergeMap(([, culture]: [Action, CultureKeyType | undefined]) => {
        return this.resourceService.getCultures(culture).pipe(
          switchMap(({ data: cultures }) => {
            cultures.forEach(
              (c) => (c.shortName = c.name.split(/[ ,]+/)[0] ?? ''),
            );
            if (
              this.gbs.growthBook.isOff(GrowthBookFeatureKeys.FEAT_US481_ADD_ES_US) || 
              this.gbs.growthBook.getFeatureValue(GrowthBookFeatureKeys.FEAT_US481_ADD_ES_US, '') === 'a'
            ) {
              cultures = cultures.filter(culture => culture.cultureCode !== CULTURE_US_SPANISH);
            }
            return of(
              resourceGetCulturesSuccess({
                cultures,
              }),
            );
          }),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.ResourcesFetchFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  getCurrencyResources$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(resourceGetAllResources, resourceGetCurrencies, updateCultureCode),
      concatLatestFrom(() => [
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
      ]),
      mergeMap(([, culture]: [Action, CultureKeyType | undefined]) => {
        return this.resourceService.getCurrencies(culture).pipe(
          switchMap(({ data: currencies }) =>
            of(
              resourceGetCurrenciesSuccess({
                currencies,
              }),
            ),
          ),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.ResourcesFetchFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  getGenderResources$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(resourceGetAllResources, resourceGetGenders, updateCultureCode),
      concatLatestFrom(() => [
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
      ]),
      mergeMap(([, culture]: [Action, CultureKeyType | undefined]) => {
        return this.resourceService.getGenders(culture).pipe(
          switchMap(({ data: genders }) =>
            of(
              resourceGetGendersSuccess({
                genders,
              }),
            ),
          ),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.ResourcesFetchFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  getSuffixResources$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(resourceGetAllResources, resourceGetSuffixes, updateCultureCode),
      concatLatestFrom(() => [
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
      ]),
      mergeMap(([, culture]: [Action, CultureKeyType | undefined]) => {
        return this.resourceService.getSuffixes(culture).pipe(
          switchMap(({ data: suffixes }) =>
            of(
              resourceGetSuffixesSuccess({
                suffixes,
              }),
            ),
          ),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.ResourcesFetchFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  getTimeZoneResources$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(resourceGetAllResources, resourceGetTimeZones, updateCultureCode),
      concatLatestFrom(() => [
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
      ]),
      mergeMap(([, culture]: [Action, CultureKeyType | undefined]) => {
        return this.resourceService.getTimeZones(culture).pipe(
          switchMap(({ data: timeZones }) =>
            of(
              resourceGetTimeZonesSuccess({
                timeZones,
              }),
            ),
          ),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.ResourcesFetchFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  constructor(
    private actions$: Actions,
    private resourceService: ResourceService,
    private store: Store,
    private gbs: GrowthBookService
  ) {}
}
